import type { FieldPolicy, Reference } from '@apollo/client';

// Cached shape of Query.getUserFeed (FeedResponse). `posts` are normalized
// Post:{id} references once written to the cache.
type FeedResponseCache = {
  __typename?: string;
  posts?: readonly Reference[];
  pageInfo?: unknown;
  feedStats?: unknown;
};

/**
 * Field policy for Query.getUserFeed (TRIL-177).
 *
 * keyArgs: one cached list per feed identity (everything except cursor/limit),
 * so pages of the same feed accumulate and different feedTypes/topics stay apart.
 *
 * merge — three cases keyed on args.cursor + whether `existing` is present:
 *  1. paginate (cursor present): append incoming, dedup by id.
 *  2. reconcile (no cursor, existing present — reconnect / background head
 *     re-fetch): prepend genuine newcomers, keep all existing refs in order.
 *     Entity data is refreshed by normalization; this preserves later pages.
 *  3. replace (no cursor, no existing — initial load, or refresh after eviction).
 */
export const getUserFeedFieldPolicy: FieldPolicy<FeedResponseCache> = {
  keyArgs: [
    'userId',
    'feedType',
    'feedTypes',
    'minRelevanceScore',
    'excludeAuthors',
    'topic',
  ],
  merge(existing, incoming, { args, readField }) {
    const incomingPosts = incoming?.posts ?? [];

    // Case 1: pagination — append + dedup by id.
    if (args?.cursor) {
      const existingPosts = existing?.posts ?? [];
      const seen = new Set<string>();
      for (const ref of existingPosts) {
        const id = readField<string>('id', ref);
        if (id) seen.add(id);
      }
      const merged: Reference[] = [...existingPosts];
      for (const ref of incomingPosts) {
        const id = readField<string>('id', ref);
        if (id && seen.has(id)) continue;
        if (id) seen.add(id);
        merged.push(ref);
      }
      return { ...incoming, posts: merged };
    }

    // Case 3: initial / post-eviction refresh — replace.
    if (!existing?.posts) {
      return incoming;
    }

    // Case 2: reconcile — prepend newcomers, keep existing order.
    const existingPosts = existing.posts;
    const existingIds = new Set<string>();
    for (const ref of existingPosts) {
      const id = readField<string>('id', ref);
      if (id) existingIds.add(id);
    }
    const newcomers: Reference[] = [];
    for (const ref of incomingPosts) {
      const id = readField<string>('id', ref);
      if (id && !existingIds.has(id)) newcomers.push(ref);
    }
    return { ...incoming, posts: [...newcomers, ...existingPosts] };
  },
};
